/**
 * CMMC scorecard model — V102-8-1 / V102-8-2 (ADR-0058; ux-adversary run
 * cmmc-self-assessment rounds 3–7; PR A critic panel). Pure: no React, no fetch.
 *
 * ONE model for every surface: the Prepare scorecard, the /programs card and
 * the Today card / deadline rows all render from `buildScorecard`, so the
 * verdict, the clock and the primary action never disagree. Every number is
 * the server's; the client picks words, order and tone.
 *
 * Rules carried from the design review and the critic panel:
 *   H1    points vs requirements: one unit per clause.
 *   H5    "Conditional: not reached" / "Conditional: reached"; while a closeout
 *         clock is under 30 days: "Conditional · at risk" (the date is said once, on the clock line).
 *   H6    "Provisional" (a tag) while the SSP is not Met or requirements are unassessed.
 *   H3-12 exactly one primary, aimed at what is at stake first; Collect evidence
 *         counts evidence-only requirements; "N of M must-Mets met" from the full set.
 *   H3-14 Level 1 is red until Met, with its lapse countdown (date said once).
 *   H3-15 one blocker list; one action per blocker, per-row links on the name.
 *   G7-H2 one days-left encoding: red < 14, amber 14–30, else neutral.
 *   G7-H3 Today carries the deadline under 30 days with its fix.
 *   H3-13 the nearest open customer deadline sits under the verdict, in the
 *         server's tone (same thresholds); a red one is also a Today row.
 */
import type { AffirmationGate, AffirmationGateStep, AffirmationRecord, CmmcScoreResult, CustomerDeadline, PoamItem, ProgramCard, UnmetRequirement } from '@aigovops/core';

export type Tone = 'red' | 'amber' | 'neutral';

const DAY = 86_400_000;

/** G7-H2: the ONE days-left encoding (clocks, lapses, Today rows, the shell). */
export function riskTone(daysLeft: number | null): Tone {
  if (daysLeft === null) return 'neutral';
  if (daysLeft < 14) return 'red';
  if (daysLeft <= 30) return 'amber';
  return 'neutral';
}

/** Whole days from `todayDay` (YYYY-MM-DD) to an ISO date or instant (negative once past). */
export function daysUntil(iso: string, todayDay: string): number {
  return Math.round((Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) - Date.parse(`${todayDay}T00:00:00Z`)) / DAY);
}

export const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const reqs = (n: number) => plural(n, 'requirement', 'requirements');
const pts = (n: number) => `${n} pt${n === 1 ? '' : 's'}`;
const days = (n: number) => plural(n, 'day', 'days');

/** "cmmc-l2:3.1.1" / "cmmc-l2:3.1.1[a]" → "3.1.1" (the reference people read). */
export const reqRef = (id: string): string => (id.includes(':') ? id.slice(id.lastIndexOf(':') + 1) : id).replace(/\[[^\]]*\]$/, '');
/** In-page anchor of a requirement row. */
export const rowAnchor = (ref: string) => `cmmc-r-${ref.replace(/[^A-Za-z0-9]+/g, '-')}`;

/** "Apr 29" this year, "Apr 29, 2025" otherwise (the fmtProgramDate rule for past or far dates). */
export function fmtWithYear(iso: string, todayDay: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  const same = iso.slice(0, 4) === todayDay.slice(0, 4);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(same ? {} : { year: 'numeric' }), timeZone: 'UTC' });
}

export function fmtShort(isoDay: string): string {
  const d = new Date(`${isoDay.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? isoDay : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

/** The period a CMMC card is scored for: the open Prepare window first, then Audit, then any active one. */
export function cmmcPeriod(card: Pick<ProgramCard, 'periods'>): { period_id: string; label: string } | null {
  const active = card.periods.filter((p) => p.active && p.period_id);
  const pick = active.find((p) => p.stage === 'prepare') ?? active.find((p) => p.stage === 'audit') ?? active[0];
  return pick ? { period_id: pick.period_id!, label: pick.label } : null;
}

/** An action: an in-page jump on the scorecard (`anchor`) or a link (`href`). */
export interface Action { label: string; href?: string; anchor?: string }
/** The link form of an action from another page: anchors open the Prepare tab at that heading. */
export const actionHref = (a: Action, prepareHref: string): string => a.href ?? `${prepareHref}#${a.anchor ?? ''}`;

export interface ReqRow {
  id: string;
  /** Anchor id (Today and the hero jump here). */
  anchor: string;
  /** Plain title first; the code is the dim suffix. */
  title: string;
  code: string;
  href: string;
  /** "5 pts", "required", "On the POA&M · closeout Oct 15", "Fixed by 3.1.20". */
  meta: string | null;
  /** "Level 1" when the row feeds a Level 1 requirement that is not met. */
  tag: string | null;
  tone?: Tone;
}

export interface Blocker {
  key: 'must_met' | 'not_assessed' | 'not_met' | 'stale' | 'l1_not_met' | 'l1_not_assessed';
  anchor: string;
  title: string;
  detail?: string;
  rows: ReqRow[];
  /** ONE action per blocker. */
  action?: { label: string; href: string };
}

export interface GateStepView { step: AffirmationGateStep; text: string; action?: { label: string; href: string } }

export interface GateView {
  locked: boolean;
  title: string;
  line: string;
  /** Only steps the blocker list doesn't cover (awaiting review, pinning). */
  steps: GateStepView[];
  action?: { label: string; href: string };
}

export interface Lapse { date: string; days: number; tone: Tone; text: string }

export interface Clock {
  kind: 'l1_lapse' | 'closeout';
  date: string;
  days: number;
  tone: Tone;
  /** "lapses Oct 14 · 8 days" / "closeout Oct 15 · 9 days". */
  text: string;
  fix: Action;
  /** The requirement the fix targets (closeout) and how many more are open. */
  ref?: string;
  more?: number;
}

export interface Verdict { text: string; tone: 'red' | 'amber' | 'ok' | 'neutral'; mark: 'square' | 'check' }

export interface ScorecardModel {
  level: '1' | '2';
  l2: null | {
    score: number;
    max: number;
    threshold: number;
    earned: number;
    notAssessedPts: number;
    notMetPts: number;
    mustMet: { total: number; met: number };
    /** "88 for conditional · 61 of 64 must-Mets met". */
    thresholdLine: string;
    /** "▲ +11 vs last posted 52" — only when a prior posted score exists. */
    delta: string | null;
  };
  l1: {
    met: boolean;
    metCount: number;
    total: number;
    lapse: Lapse | null;
    /** The Level 1 line on a Level 2 hero ("Level 1 · Not yet · 12 of 15 met"), linking to the rows that feed it. */
    line: { text: string; tone: Tone; anchor: string | null } | null;
  };
  verdict: Verdict;
  /** H3-13: the nearest open customer deadline ("Halvorsen needs Level 2 conditional by Jan 15 · 102 days"). */
  customer: { text: string; tone: Tone; deadline: CustomerDeadline } | null;
  provisional: string | null;
  clock: Clock | null;
  /** While a closeout clock is under 30 days: the clock as one toned line, and who depends on it. */
  closeout: { text: string; tone: Tone; note: string | null } | null;
  /** The blocker list heading ("What could lose conditional status · 4 on the POA&M"). */
  blockersHeading: string;
  primary: Action | null;
  secondary: Action | null;
  blockers: Blocker[];
  gate: GateView | null;
  /** The score is ready to post (no status): whether posting leads, and where it opens. */
  postReady: { leads: boolean; href: string } | null;
  mustMetCount: number;
  /** Level 1 profile: judged not met vs not assessed (the Today subtitle). */
  l1Counts: { notMet: number; unassessed: number } | null;
  /** For "N of M programs need attention". */
  attention: boolean;
}

export interface ScorecardInput {
  score: CmmcScoreResult;
  gate: AffirmationGate | null;
  /** Requirement titles by requirement id (objective readiness), when loaded. */
  titles?: ReadonlyMap<string, string>;
  affirmations?: readonly AffirmationRecord[];
  poam?: readonly PoamItem[];
  /** Customer deadlines evaluated against this period's score (GET …/customer-requirements?period_id=). */
  deadlines?: readonly CustomerDeadline[];
  todayDay: string;
  prefix: string;
  program: string;
  periodId: string;
  /** Program tab hrefs (Audit holds fieldwork / pinning and the affirmation). */
  tabHref: (tab: 'prepare' | 'audit' | 'report') => string;
}

/** The Level 1 lapse: the latest affirmed Level 1 affirmation's next due date. */
export function level1Lapse(affirmations: readonly AffirmationRecord[] | undefined, todayDay: string): Lapse | null {
  const due = (affirmations ?? []).filter((a) => a.level === '1' && a.state === 'affirmed' && a.next_due_at).map((a) => a.next_due_at!).sort();
  const date = due[due.length - 1];
  if (!date) return null;
  const n = daysUntil(date, todayDay);
  return { date: date.slice(0, 10), days: n, tone: riskTone(n), text: n < 0 ? `lapsed ${fmtShort(date)}` : n === 0 ? `lapses today` : `lapses ${fmtShort(date)} · ${days(n)}` };
}

/** "▲ +11 vs last posted 52": the latest affirmed Level 2 score of an earlier period. Null without one (never invented). */
export function lastPostedDelta(score: number, affirmations: readonly AffirmationRecord[] | undefined, periodId: string): string | null {
  const prior = (affirmations ?? [])
    .filter((a) => a.level === '2' && a.state === 'affirmed' && a.score !== null && a.period_id !== periodId && a.affirmed_at)
    .sort((a, b) => (a.affirmed_at! < b.affirmed_at! ? 1 : -1))[0];
  if (!prior) return null;
  const d = score - prior.score!;
  return d === 0 ? `Same as last posted ${prior.score}` : `${d > 0 ? '▲ +' : '▼ −'}${Math.abs(d)} vs last posted ${prior.score}`;
}

const requiredWords = (d: Pick<CustomerDeadline, 'required_level' | 'required_status'>) =>
  d.required_level === '1' ? 'Level 1 met' : `Level 2 ${d.required_status}`;

/** H3-13: the nearest open customer deadline in words, in the server's tone. */
export function customerLine(deadlines: readonly CustomerDeadline[] | undefined): ScorecardModel['customer'] {
  const d = (deadlines ?? []).find((x) => !x.met);
  if (!d) return null;
  const when = d.missed ? 'missed' : d.days_left === 0 ? 'today' : days(d.days_left);
  return { text: `${d.customer_name} needs ${requiredWords(d)} by ${fmtShort(d.required_by)} · ${when}`, tone: d.tone === 'met' ? 'neutral' : d.tone, deadline: d };
}

const isMustMet = (u: Pick<UnmetRequirement, 'poam_eligibility'>) => u.poam_eligibility === 'never' || u.poam_eligibility === 'ineligible';

export function buildScorecard(input: ScorecardInput): ScorecardModel {
  const { score, gate, titles, todayDay, prefix, program, periodId, tabHref } = input;
  const ctx = `program=${encodeURIComponent(program)}&period=${encodeURIComponent(periodId)}`;
  const back = encodeURIComponent(tabHref('prepare'));
  const findingsHref = (ids: readonly string[]) => `${prefix}/findings?requirement=${ids.map(reqRef).join(',')}&${ctx}&return=${back}`;
  const evidenceHref = `${prefix}/evidence?${ctx}&need=unassessed`;
  const staleHref = `${prefix}/evidence?${ctx}&filter=stale`;
  const title = (id: string) => titles?.get(id) ?? reqRef(id);
  const audit = tabHref('audit');
  // N3: a group's action opens that group in Gaps, where each row has its own fix.
  const gapsHref = (anchor: string) => `${tabHref('prepare')}?view=gaps#${anchor}`;

  const l1 = score.level1;
  const l1Met = l1.status === 'met';
  const lapse = level1Lapse(input.affirmations, todayDay);
  // Level 2 rows that feed a Level 1 requirement that is not met.
  const feedsL1 = new Set(l1.requirements.filter((r) => r.status === 'not_met').flatMap((r) => r.failing));

  // Gate readiness: finished, something may be signed, and not already signed for this period.
  const signedHere = (input.affirmations ?? []).find((a) => a.period_id === periodId && a.state === 'affirmed' && (gate?.allowed_kinds ?? []).includes(a.kind));
  const gateReady = !!gate && gate.finished && gate.allowed_kinds.length > 0 && !signedHere;
  const gateAction: Action | null = gateReady
    // Until the signing flow (V102-8-6) the primary says where it leads.
    ? { label: gate!.at_status ? 'Ready to affirm · open Audit' : 'Ready to post · open Audit', href: audit }
    : null;

  const blockers: Blocker[] = [];
  let l2: ScorecardModel['l2'] = null;
  let clock: Clock | null = null;
  let verdict: Verdict;
  let provisional: string | null = null;
  const fixes: Action[] = [];
  let collect: Action | null = null;
  let l1Line: ScorecardModel['l1']['line'] = null;
  let l1Counts: { notMet: number; unassessed: number } | null = null;

  const l2r = score.level2;
  if (l2r) {
    const notAssessedPts = l2r.points_not_yet_assessed;
    const notMetPts = Math.max(0, l2r.max_score - l2r.score - notAssessedPts);
    const mustMet = l2r.unmet.filter((u) => u.assessed && isMustMet(u));
    const eligible = l2r.unmet.filter((u) => u.assessed && !isMustMet(u));
    const notAssessed = l2r.unmet.filter((u) => !u.assessed);
    const evidenceOnly = notAssessed.filter((u) => l2r.closed_by_collection_alone.includes(u.requirement_id));
    const waiting = notAssessed.filter((u) => !l2r.closed_by_collection_alone.includes(u.requirement_id));
    const evidencePts = evidenceOnly.reduce((n, u) => n + u.points, 0);
    const waitingPts = waiting.reduce((n, u) => n + u.points, 0);
    const notAssessedL1 = notAssessed.filter((u) => feedsL1.has(u.requirement_id)).length;
    const mustSet = l2r.must_met ?? { total: 0, met: 0 };

    // The closeout clock: the nearest open POA&M item while conditional status depends on it.
    if (l2r.status === 'conditional' || l2r.status === 'conditional_lapsed') {
      const open = (input.poam ?? []).filter((p) => p.status === 'open' && p.closeout_due_at).sort((a, b) => (a.closeout_due_at! < b.closeout_due_at! ? -1 : 1));
      const next = open[0];
      if (next) {
        const n = daysUntil(next.closeout_due_at!, todayDay);
        const ref = reqRef(next.requirement_id);
        clock = {
          kind: 'closeout', date: next.closeout_due_at!.slice(0, 10), days: n, tone: riskTone(n), ref, more: open.length - 1,
          text: n < 0 ? `closeout passed ${fmtShort(next.closeout_due_at!)}` : `closeout ${fmtShort(next.closeout_due_at!)} · ${days(n)}`,
          fix: { label: `Close ${ref}`, anchor: rowAnchor(ref) },
        };
      }
    }

    verdict = l2r.status === 'final' ? { text: 'Final: reached', tone: 'ok', mark: 'check' }
      : l2r.status === 'conditional_lapsed' ? { text: 'Conditional: lapsed', tone: 'red', mark: 'square' }
      : l2r.status === 'conditional'
        ? (clock && clock.days < 30
          ? { text: 'Conditional · at risk', tone: clock.tone === 'red' ? 'red' : 'amber', mark: 'square' }
          : { text: 'Conditional: reached', tone: 'ok', mark: 'check' })
        : { text: 'Conditional: not reached', tone: 'red', mark: 'square' };

    l2 = {
      score: l2r.score, max: l2r.max_score, threshold: l2r.conditional_threshold,
      earned: Math.max(0, l2r.score), notAssessedPts, notMetPts, mustMet: mustSet,
      thresholdLine: mustSet.total
        ? `${l2r.conditional_threshold} for conditional · ${mustSet.met} of ${mustSet.total} must-Mets met`
        : `${l2r.conditional_threshold} for conditional`,
      delta: lastPostedDelta(l2r.score, input.affirmations, periodId),
    };
    const why: string[] = [];
    if (!l2r.ssp_present) why.push('SSP not Met');
    if (notAssessed.length) why.push(`${reqs(notAssessed.length)} not assessed`);
    provisional = why.length ? ['Provisional', ...why].join(' · ') : null;

    const row = (u: UnmetRequirement, meta: string | null): ReqRow => {
      const ref = reqRef(u.requirement_id);
      const ssp = u.requirement_id === l2r.ssp_requirement_id;
      return {
        id: u.requirement_id, anchor: rowAnchor(ref), title: title(u.requirement_id), code: ref,
        href: ssp ? `${prefix}/policies?new=ssp&return=${back}` : findingsHref([u.requirement_id]),
        meta: ssp ? 'required' : meta, tag: feedsL1.has(u.requirement_id) ? 'Level 1' : null,
      };
    };

    if (mustMet.length) {
      blockers.push({
        key: 'must_met', anchor: 'cmmc-b-must', title: `${reqs(mustMet.length)} must be Met · can't go on POA&M`,
        rows: mustMet.map((u) => row(u, pts(u.points))),
        action: { label: `Fix the ${mustMet.length} in Gaps`, href: gapsHref('gaps-must') },
      });
      fixes.push({ label: `Fix the ${mustMet.length} must-Met${mustMet.length === 1 ? '' : 's'}`, anchor: 'cmmc-b-must' });
    }
    if (notAssessed.length) {
      const lines: ReqRow[] = [];
      if (evidenceOnly.length) {
        lines.push({ id: 'evidence-only', anchor: 'cmmc-r-evidence-only', title: `${evidenceOnly.length} need evidence only`, code: '', href: evidenceHref, meta: pts(evidencePts), tag: null });
      }
      if (waiting.length) {
        const awaiting = gate?.counts.awaiting_review ?? 0;
        lines.push({
          id: 'waiting', anchor: 'cmmc-r-waiting', title: awaiting ? plural(waiting.length, 'result awaiting review', 'results awaiting review') : `${waiting.length} waiting for a recorded result`, code: '',
          href: awaiting ? '#cmmc-review' : findingsHref(waiting.map((u) => u.requirement_id)), meta: pts(waitingPts), tag: null,
        });
      }
      blockers.push({
        key: 'not_assessed', anchor: 'cmmc-b-unassessed',
        title: `${reqs(notAssessed.length)} not assessed · ${pts(notAssessedPts)}${notAssessedL1 ? ` · incl. ${notAssessedL1} Level 1` : ''}`,
        rows: lines, action: evidenceOnly.length ? { label: 'Collect evidence', href: evidenceHref } : undefined,
      });
      // N10: the hero's Collect goes straight to the evidence the points need.
      if (evidenceOnly.length) collect = { label: `Collect evidence · +${pts(evidencePts)}`, href: evidenceHref };
    }
    if (eligible.length) {
      const due = new Map((input.poam ?? []).filter((p) => p.status === 'open').map((p) => [p.requirement_id, p.closeout_due_at] as const));
      const sorted = [...eligible].sort((a, b) => {
        const da = due.get(a.requirement_id) ?? '9999', db = due.get(b.requirement_id) ?? '9999';
        return da < db ? -1 : da > db ? 1 : 0;
      });
      blockers.push({
        key: 'not_met', anchor: 'cmmc-b-notmet', title: `${reqs(eligible.length)} not met · ${pts(eligible.reduce((n, u) => n + u.points, 0))} · can go on POA&M`,
        rows: sorted.map((u) => {
          const d = due.get(u.requirement_id);
          const r = row(u, d ? `On the POA&M · closeout ${fmtShort(d)}` : u.poam_id ? 'On the POA&M' : pts(u.points));
          return d ? { ...r, tone: riskTone(daysUntil(d, todayDay)) } : r;
        }),
        action: { label: `Fix the ${eligible.length} in Gaps`, href: gapsHref('gaps-poam') },
      });
      if (l2r.score < l2r.conditional_threshold) fixes.push({ label: `Fix the ${eligible.length} not met`, anchor: 'cmmc-b-notmet' });
    }
    if (!l1Met) {
      const first = l2r.unmet.find((u) => feedsL1.has(u.requirement_id));
      l1Line = {
        text: `Level 1 · Not yet${lapse ? ` · ${lapse.text}` : ''} · ${l1.counts.met} of ${l1.counts.requirements} met`,
        tone: 'red', anchor: first ? rowAnchor(reqRef(first.requirement_id)) : null,
      };
    }
  } else {
    // Level 1 profile: the requirements not met, split judged-not-met vs not assessed yet.
    const unassessed = new Set(l1.not_yet_assessed);
    const notMet = l1.requirements.filter((r) => r.status === 'not_met');
    const judged = notMet.filter((r) => r.failing.some((id) => !unassessed.has(id)));
    const waiting = notMet.filter((r) => !judged.includes(r));
    const l1Row = (r: typeof notMet[number], href: string): ReqRow => {
      const code = reqRef(r.requirement_id);
      const lead = r.failing[0] ?? r.l2[0] ?? r.requirement_id;
      return {
        id: r.requirement_id, anchor: rowAnchor(code), title: title(lead), code, href,
        meta: `Fixed by ${r.failing.map(reqRef).join(', ')}`, tag: null,
      };
    };
    if (l1.not_yet_assessed.length) provisional = `Provisional · ${reqs(l1.not_yet_assessed.length)} not assessed`;
    if (lapse && !l1Met) {
      clock = { kind: 'l1_lapse', date: lapse.date, days: lapse.days, tone: lapse.tone, text: lapse.text,
        fix: { label: `Close the ${notMet.length}`, anchor: 'cmmc-blockers-h' } };
    }
    verdict = l1Met
      ? { text: 'Met', tone: 'ok', mark: 'check' }
      : { text: lapse ? `Not yet · ${lapse.text}` : 'Not yet', tone: 'red', mark: 'square' };
    if (judged.length) {
      blockers.push({
        key: 'l1_not_met', anchor: 'cmmc-b-l1', title: `Not met · ${judged.length}`,
        rows: judged.map((r) => l1Row(r, findingsHref(r.failing))),
        action: { label: `Fix the ${judged.length} in Gaps`, href: gapsHref('gaps-must') },
      });
    }
    if (waiting.length) {
      blockers.push({
        key: 'l1_not_assessed', anchor: 'cmmc-b-l1-unassessed', title: `Not assessed · ${waiting.length}`,
        rows: waiting.map((r) => l1Row(r, evidenceHref)),
        action: { label: 'Collect evidence', href: evidenceHref },
      });
      collect = { label: `Collect evidence · ${reqs(waiting.length)}`, href: evidenceHref };
    }
    if (notMet.length) fixes.push({ label: `Close the ${notMet.length}`, anchor: 'cmmc-blockers-h' });
    l1Counts = { notMet: judged.length, unassessed: waiting.length };
  }

  // Stale Mets block finishing: their own fix (H3-33), shown once.
  if (gate && gate.counts.stale > 0) {
    blockers.push({
      key: 'stale', anchor: 'cmmc-b-stale', title: `${plural(gate.counts.stale, 'Met result', 'Met results')} on stale evidence · blocks finishing`, rows: [],
      action: { label: `Review ${gate.counts.stale} stale`, href: staleHref },
    });
  }

  // Exactly one primary: what is at stake first (a clock under 30 days), then a ready signature,
  // then blocker #1, then evidence, then the gate's own steps.
  const steps: Action[] = [];
  if (gate && !gate.finished) {
    if (gate.open_steps.includes('awaiting_review')) steps.push({ label: `Review ${plural(gate.counts.awaiting_review, 'result', 'results')}`, anchor: 'cmmc-review' });
    if (gate.open_steps.includes('not_pinned')) steps.push({ label: 'Start fieldwork at Audit', href: audit });
  }
  // R3-H1: a ready signature leads only when it meets the open customer requirement (at status), or there
  // is none. Otherwise blocker #1 leads and posting the current score is the secondary ("Post 84 now").
  const openCustomer = (input.deadlines ?? []).find((d) => !d.met) ?? null;
  const postingLeads = !!gateAction && (gate!.at_status || !openCustomer);
  const postNow: Action | null = gateAction && !postingLeads ? { label: `Post ${l2r?.score ?? ''} now · open Audit`.replace('Post  now', 'Post now'), href: audit } : null;
  const ordered = [clock && clock.days < 30 ? clock.fix : null, postingLeads ? gateAction : null, fixes[0] ?? null, postNow, ...fixes.slice(1), collect, ...steps]
    .filter((a): a is Action => !!a);
  const uniq = ordered.filter((a, i) => ordered.findIndex((b) => (b.anchor ?? b.href) === (a.anchor ?? a.href)) === i);
  const primary = uniq[0] ?? null;
  // The secondary: Collect when blocker #1 leads, else the next distinct fix (or the ready signature).
  const secondary = primary ? (uniq.find((a) => a !== primary && a === collect) ?? uniq.find((a) => a !== primary && a !== collect && !steps.includes(a)) ?? null) : null;

  const customer = customerLine(input.deadlines);
  // While the closeout clock is under 30 days: one toned line with the clock, and the customer that depends on it.
  const dependent = (input.deadlines ?? []).find((d) => d.required_level === '2');
  const closeout = clock && clock.kind === 'closeout' && clock.days < 30
    ? { text: `POA&M ${clock.text}`, tone: clock.tone, note: dependent ? `${dependent.customer_name} requirement depends on this closeout` : null }
    : null;
  const blockersHeading = !l2r ? 'What’s stopping Level 1'
    : (l2r.status === 'conditional' || l2r.status === 'final')
      ? `What could lose conditional status${l2r.poam.open ? ` · ${l2r.poam.open} on the POA&M` : ''}`
      : 'What’s stopping conditional status';
  const attention = (l2r
    ? !(l2r.status === 'conditional' || l2r.status === 'final') || (!!clock && clock.tone !== 'neutral')
    : !l1Met || (!!clock && clock.tone !== 'neutral')) || (!!customer && customer.tone !== 'neutral');

  return {
    level: score.level, l2,
    l1: { met: l1Met, metCount: l1.counts.met, total: l1.counts.requirements, lapse, line: l1Line },
    verdict, customer, provisional, clock, closeout, blockersHeading, primary, secondary, blockers, l1Counts,
    gate: gate ? gateView(gate, audit, {
      ready: gateReady, primaryIsGate: (!!gateAction && primary === gateAction) || (!!postNow && (primary === postNow || secondary === postNow)),
      signedHere, score: l2r?.score, threshold: l2r?.conditional_threshold,
    }) : null,
    postReady: gateAction && !gate!.at_status ? { leads: postingLeads, href: audit } : null,
    mustMetCount: blockers.find((b) => b.key === 'must_met')?.rows.length ?? 0,
    attention,
  };
}

const STEP_TEXT = (g: AffirmationGate, step: AffirmationGateStep): string =>
  step === 'awaiting_review' ? `${plural(g.counts.awaiting_review, 'result', 'results')} awaiting review` : 'Tests not pinned yet';

/**
 * The affirmation gate as a card: words only. Locked: "Opens when the
 * self-assessment is finished" plus ONLY the steps the blocker list doesn't
 * cover (results awaiting review, pinning at Audit). Ready: what may be
 * signed; its action shows here only when it isn't already the hero's primary.
 */
export function gateView(gate: AffirmationGate, auditHref: string, o: { ready?: boolean; primaryIsGate?: boolean; signedHere?: AffirmationRecord; score?: number; threshold?: number } = {}): GateView {
  const title = gate.level === '1' ? 'Annual affirmation' : 'Affirmation';
  if (!gate.finished) {
    const steps = gate.open_steps.filter((s) => s === 'awaiting_review' || s === 'not_pinned').map<GateStepView>((s) => ({
      step: s, text: STEP_TEXT(gate, s), ...(s === 'not_pinned' ? { action: { label: 'Start fieldwork at Audit', href: auditHref } } : {}),
    }));
    return { locked: true, title, line: 'Opens when the self-assessment is finished', steps };
  }
  if (o.signedHere?.affirmed_at) return { locked: false, title, line: `Signed ${fmtWithYear(o.signedHere.affirmed_at, new Date().toISOString().slice(0, 10))}`, steps: [] };
  const what = gate.at_status
    ? (gate.level === '1' ? 'Ready to affirm Level 1' : 'Ready to affirm conditional status')
    : gate.allowed_kinds.includes('score_post')
      ? (o.score !== undefined ? `Ready to post · ${o.score} pts (below ${o.threshold ?? 88}, no conditional status)` : 'Ready to post · no conditional status')
      : 'Not at status yet';
  return { locked: false, title, line: what, steps: [], ...(o.ready && !o.primaryIsGate ? { action: { label: 'Open in Audit', href: auditHref } } : {}) };
}

// ---- Today ------------------------------------------------------------------------------------

export interface TodayItem {
  /** A second, quieter action (finished below status: posting now). */
  secondary?: { label: string; href: string };
  key: string;
  title: string;
  detail: string;
  tone: Tone;
  action: { label: string; href: string };
}

/**
 * G7-H3 + panel F6: the CMMC rows on Today — a clock under 30 days (red < 14,
 * amber 14–30) with its fix, and a score ready to post. Red first.
 */
export function cmmcTodayItems(m: ScorecardModel, prepareHref: string, programName = 'CMMC'): TodayItem[] {
  const out: TodayItem[] = [];
  const c = m.clock;
  // "· Brightline needs it Oct 28" — the open customer deadline, said once.
  const cust = m.customer ? ` · ${m.customer.deadline.customer_name} needs it ${fmtShort(m.customer.deadline.required_by)}` : '';
  if (c && c.days < 30) {
    // R2: the row names the program ("CMMC Level 1 lapses in 8 days").
    const title = c.kind === 'l1_lapse'
      ? (c.days < 0 ? `${programName} Level 1 lapsed ${fmtShort(c.date)}` : `${programName} Level 1 lapses in ${days(c.days)}`)
      : (c.days < 0 ? `${programName} POA&M closeout passed · conditional status lapsed` : `${programName} POA&M closeout ends in ${days(c.days)}`);
    const lc = m.l1Counts;
    const detail = c.kind === 'l1_lapse'
      ? [lc?.notMet ? `${lc.notMet} not met` : null, lc?.unassessed ? `${lc.unassessed} need evidence` : null].filter(Boolean).join(' · ') + cust
      : `${c.ref} still open${c.more ? ` · ${c.more} more` : ''}${cust}`;
    const label = c.kind === 'closeout' ? `Close ${c.ref}` : c.fix.label;
    out.push({ key: c.kind, title, detail, tone: c.tone, action: { label, href: actionHref(c.fix, prepareHref) } });
  }
  // H3-13: a red customer deadline (under 14 days, or missed) is a Today row too; its fix is the scorecard's primary.
  const cu = m.customer;
  if (cu && cu.tone === 'red') {
    const d = cu.deadline;
    out.push({
      key: `customer-${d.customer_requirement_id}`, tone: 'red',
      title: d.missed ? `${programName} · ${d.customer_name} deadline missed` : `${programName} · ${d.customer_name} needs ${requiredWords(d)} in ${days(d.days_left)}`,
      detail: `By ${fmtShort(d.required_by)}`,
      action: m.primary ? { label: m.primary.label, href: actionHref(m.primary, prepareHref) } : { label: 'Open the scorecard', href: prepareHref },
    });
  }
  const pr = m.postReady;
  if (pr && m.l2) {
    if (!pr.leads && m.customer && m.primary) {
      // R4: one posting label everywhere — while a requirement is open it is "Post 84 now".
      const post = { label: `Post ${m.l2.score} now · open Audit`, href: pr.href };
      // R3-H1: below 88 with a customer requirement open — the fix leads; posting is the quiet second.
      const d = m.customer.deadline;
      out.push({
        // R4: the shared days-left tone of the open requirement (red only under 14 days), not a fixed red.
        key: 'post-at-risk', tone: m.customer.tone,
        title: `${programName} · ${m.l2.score} of ${m.l2.threshold} for conditional · ${d.customer_name} ${fmtShort(d.required_by)}`,
        detail: m.mustMetCount ? `${plural(m.mustMetCount, 'must-Met', 'must-Mets')} not met · posting now records no conditional status` : 'Posting now records no conditional status',
        action: { label: m.primary.label, href: actionHref(m.primary, prepareHref) }, secondary: post,
      });
    } else {
      out.push({ key: 'post-ready', title: `${programName} · score ready to post`, detail: `Self-assessment finished · ${m.l2.score} pts / ${m.l2.max}`, tone: 'neutral',
        action: { label: 'Ready to post · open Audit', href: pr.href } });
    }
  }
  const rank: Record<Tone, number> = { red: 0, amber: 1, neutral: 2 };
  return out.sort((a, b) => rank[a.tone] - rank[b.tone]);
}
